import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EconomicCalendar = () => {
  const { t } = useLanguage();
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState('today');
  const [importance, setImportance] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, importance]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const now = new Date();
      const params = {};
      if (filter === 'today') {
        const start = new Date(now); start.setHours(0,0,0,0);
        const end = new Date(now); end.setHours(23,59,59,999);
        params.date_from = start.toISOString();
        params.date_to = end.toISOString();
      } else if (filter === 'tomorrow') {
        const start = new Date(now); start.setDate(now.getDate()+1); start.setHours(0,0,0,0);
        const end = new Date(start); end.setHours(23,59,59,999);
        params.date_from = start.toISOString();
        params.date_to = end.toISOString();
      } else if (filter === 'week') {
        params.date_from = now.toISOString();
        const end = new Date(now); end.setDate(now.getDate()+7);
        params.date_to = end.toISOString();
      }
      if (importance) params.importance = importance;
      const response = await axios.get(`${API}/economic-calendar/events`, { params });
      setEvents(response.data);
    } catch (e) {
      console.error('Failed to fetch calendar');
    } finally {
      setLoading(false);
    }
  };

  const importanceBadge = (imp) => {
    const map = {
      HIGH: 'bg-red-100 text-red-800',
      MEDIUM: 'bg-yellow-100 text-yellow-800',
      LOW: 'bg-slate-100 text-slate-600',
    };
    const label = { HIGH: t('high'), MEDIUM: t('medium'), LOW: t('low') }[imp] || imp;
    return <span className={`px-2 py-1 rounded text-xs font-semibold ${map[imp] || map.LOW}`}>{label}</span>;
  };

  return (
    <section id="economic-calendar" className="py-24 px-6 bg-slate-50" data-testid="economic-calendar-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest text-brand-accent font-bold mb-3">Data & Events</div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">{t('economicCalendar')}</h2>
          <p className="text-slate-600 mt-2">{t('economicCalendarSubtitle')}</p>
          <p className="text-xs text-yellow-600 mt-2"></p>
        </div>

        <div className="flex flex-wrap gap-2 mb-6 items-center justify-center">
          {['today', 'tomorrow', 'week', 'all'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`calendar-filter-${f}`}
              className={`px-4 py-2 rounded-full text-sm font-semibold ${
                filter === f ? 'bg-brand-primary text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-100'
              }`}
            >
              {f === 'today' ? t('filterToday') : f === 'tomorrow' ? t('filterTomorrow') : f === 'week' ? t('filterWeek') : t('filterAll')}
            </button>
          ))}
          <select
            value={importance}
            onChange={e => setImportance(e.target.value)}
            data-testid="calendar-importance-filter"
            className="px-4 py-2 rounded-full text-sm font-semibold bg-white border border-slate-200 text-slate-700"
          >
            <option value="">All Importance</option>
            <option value="HIGH">{t('high')}</option>
            <option value="MEDIUM">{t('medium')}</option>
            <option value="LOW">{t('low')}</option>
          </select>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm" data-testid="calendar-events-table">
            <thead className="bg-slate-50">
              <tr className="text-left uppercase text-xs tracking-wider text-slate-600">
                <th className="p-4">{t('dateCol')}</th>
                <th className="p-4">{t('timeCol')}</th>
                <th className="p-4">{t('currencyCol')}</th>
                <th className="p-4">{t('countryCol')}</th>
                <th className="p-4">{t('eventCol')}</th>
                <th className="p-4">{t('importanceCol')}</th>
                <th className="p-4 text-right">{t('previousCol')}</th>
                <th className="p-4 text-right">{t('forecastCol')}</th>
                <th className="p-4 text-right">{t('actualCol')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? <tr><td colSpan="9" className="p-8 text-center"><div className="loader mx-auto"></div></td></tr>
                : events.length === 0 ? <tr><td colSpan="9" className="p-8 text-center text-slate-500">No events scheduled</td></tr>
                : events.map(evt => {
                    const dt = new Date(evt.event_time);
                    return (
                      <tr key={evt.id} className="border-t border-slate-100 hover:bg-slate-50">
                        <td className="p-4 text-xs">{dt.toLocaleDateString()}</td>
                        <td className="p-4 text-xs">{dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</td>
                        <td className="p-4 font-semibold">{evt.currency}</td>
                        <td className="p-4">{evt.country}</td>
                        <td className="p-4">{evt.event}</td>
                        <td className="p-4">{importanceBadge(evt.importance)}</td>
                        <td className="p-4 text-right tabular-nums">{evt.previous ?? '-'}</td>
                        <td className="p-4 text-right tabular-nums">{evt.forecast ?? '-'}</td>
                        <td className="p-4 text-right tabular-nums font-semibold">{evt.actual ?? '-'}</td>
                      </tr>
                    );
                  })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default EconomicCalendar;
